import React from 'react';
import LegalLayout from '../components/layout/LegalLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiTrash2, 
  FiSmartphone, 
  FiMail, 
  FiShield, 
  FiClock, 
  FiDatabase,
  FiArrowRight,
  FiInfo
} = FiIcons;

const navLinks = [
  { label: 'How to Delete', href: '#methods' },
  { label: 'What data is deleted', href: '#what-deleted' },
  { label: 'Data retention', href: '#retention' },
  { label: 'Important notes', href: '#notes' },
];

const SectionIcon = ({ icon, className = "text-rose bg-paper border-line" }) => (
  <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center mb-6 shadow-sm ${className}`}>
    <SafeIcon icon={icon} className="w-6 h-6" />
  </div>
);

const AccountDeletion = () => {
  return (
    <LegalLayout 
      title="Delete Your Nisaa Account" 
      lastUpdated="19 June 2026" 
      navLinks={navLinks}
    >
      <div className="mb-16">
        <p className="text-xl text-muted leading-relaxed font-light">
          Nisaa is operated by <strong>Sussex Endocrine Clinic</strong>. We respect your right to privacy and provide simple, transparent ways to permanently delete your account and all associated health and spiritual data.
        </p>
      </div>

      {/* 1. How to Delete */}
      <section id="methods" className="scroll-mt-32">
        <SectionIcon icon={FiTrash2} className="text-rose bg-rose/10 border-rose/20" />
        <h2>How to Delete Your Account</h2>
        <p className="mb-8">You can request the deletion of your account and data through either of the following methods:</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 not-prose mb-12">
          <div className="p-8 rounded-[32px] bg-white border border-line shadow-soft">
            <div className="w-10 h-10 rounded-xl bg-aubergine text-white flex items-center justify-center mb-6">
              <SafeIcon icon={FiSmartphone} className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-bold text-aubergine mb-3">In-App Deletion</h3>
            <p className="text-sm text-muted mb-6">The fastest way to remove your data is directly within the Nisaa app.</p>
            <ol className="text-sm text-muted space-y-2 list-decimal pl-4">
              <li>Open the <strong>Nisaa</strong> app</li>
              <li>Go to <strong>Profile</strong> settings</li>
              <li>Select <strong>Account</strong></li>
              <li>Tap <strong>Delete Account</strong></li>
            </ol>
          </div>

          <div className="p-8 rounded-[32px] bg-white border border-line shadow-soft">
            <div className="w-10 h-10 rounded-xl bg-rose text-white flex items-center justify-center mb-6">
              <SafeIcon icon={FiMail} className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-bold text-aubergine mb-3">Email Request</h3>
            <p className="text-sm text-muted mb-6">If you cannot access the app, you can request deletion via email.</p>
            <p className="text-sm text-muted mb-4">Email us from the address linked to your account:</p>
            <a 
              href="mailto:contact@sussexendocrineclinic.co.uk?subject=Delete%20Nisaa%20Account" 
              className="text-rose font-bold hover:underline break-all"
            >
              contact@sussexendocrineclinic.co.uk
            </a>
            <p className="text-xs text-muted mt-4 italic">Subject: "Delete Nisaa Account"</p>
          </div>
        </div>
      </section>

      {/* 2. What is Deleted */}
      <section id="what-deleted" className="scroll-mt-32">
        <SectionIcon icon={FiDatabase} />
        <h2>What data is deleted?</h2>
        <p>When your account is deleted, we permanently erase the following information from our servers:</p>
        <ul>
          <li><strong>Account Profile:</strong> Your email address and authentication records.</li>
          <li><strong>Health Logs:</strong> All period, symptom, and mood data stored in cloud backups.</li>
          <li><strong>Pregnancy Data:</strong> Due dates, baby movement logs, and personal pregnancy notes.</li>
          <li><strong>Spiritual Records:</strong> Prayer settings, fasting logs, and ghusl history.</li>
          <li><strong>App Settings:</strong> All personalized preferences and notification settings.</li>
        </ul>
      </section>

      {/* 3. Data Retention */}
      <section id="retention" className="scroll-mt-32">
        <SectionIcon icon={FiClock} />
        <h2>Processing & Retention</h2>
        <p>
          <strong>Immediate Deletion:</strong> In-app deletion requests are processed immediately. Once confirmed, your data is removed from our live databases instantly.
        </p>
        <p>
          <strong>Email Requests:</strong> For security, we must verify your identity before processing email requests. These are typically handled within <strong>30 days</strong>.
        </p>
        <div className="my-8 p-6 bg-paper border border-line rounded-2xl">
          <h3 className="m-0 text-sm font-bold uppercase tracking-wider text-muted mb-2 flex items-center gap-2">
            <SafeIcon icon={FiShield} className="w-4 h-4" /> Exception for Legal Requirements
          </h3>
          <p className="m-0 text-sm text-muted italic">
            We may retain limited records (such as transaction IDs for subscription billing or security logs) only where required for legal, regulatory, or fraud prevention reasons as permitted by UK law.
          </p>
        </div>
      </section>

      {/* 4. Important Notes */}
      <section id="notes" className="scroll-mt-32">
        <SectionIcon icon={FiInfo} />
        <h2>Important Considerations</h2>
        <ul>
          <li><strong>Irreversibility:</strong> Account deletion is permanent. Once deleted, your data cannot be recovered by our team.</li>
          <li><strong>Local Data:</strong> Deleting your cloud account does not automatically clear the local data on your smartphone. To remove local data, you must uninstall the app or clear the app's cache in your device settings.</li>
          <li><strong>Subscriptions:</strong> If you have an active subscription managed by Apple or Google, you must cancel it through their respective stores to prevent future billing. Deleting your Nisaa account does not automatically cancel third-party app store subscriptions.</li>
        </ul>
      </section>

      <div className="mt-16 pt-8 border-t border-line text-center">
        <p className="text-muted text-sm mb-4">Back to home or read our privacy policy</p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href="/#/" className="text-aubergine font-bold hover:text-rose transition-colors">Home</a>
          <span className="text-line">•</span>
          <a href="/#/privacy" className="text-aubergine font-bold hover:text-rose transition-colors">Privacy Policy</a>
        </div>
      </div>
    </LegalLayout>
  );
};

export default AccountDeletion;